if (!customElements.get('shade-selector')) {
  // Regions patched individually on shade swap instead of replacing the whole
  // section, so apps/custom elements outside these regions keep their state.
  // 'outer' replaces the matched element itself; 'inner' only replaces its
  // children, keeping the element (and any listeners bound to it) alive.
  const PATCH_REGIONS = [
    { selector: '.product__title', mode: 'outer' },
    { selector: '.price-block', mode: 'outer' },
    { selector: 'variant-radios', mode: 'outer' },
    { selector: 'variant-selects', mode: 'outer' },
    { selector: '.payment_services', mode: 'outer' },
    { selector: 'shade-selector.available-shades', mode: 'outer' },
    { selector: 'media-gallery', mode: 'outer' },
  ];

  customElements.define(
    'shade-selector',
    class ShadeSelector extends HTMLElement {
      constructor() {
        super();
        this.sections = (this.dataset.sections || '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);

        this.controller = null;
        this.onclick = this.onClick.bind(this);

        this.summaryButton = this.querySelector('.available-shades__buttons summary');
        this.summaryLabel = this.querySelector('.available-shades__buttons-label');
        this.summaryTarget = this.querySelector('#available-shades');
        this.initSummaryClick();
      }

      connectedCallback() {
        this.restoreLastGroup();
      }

      restoreLastGroup() {
        let lastGroup = null;

        if (typeof window !== 'undefined') {
          lastGroup = window.__shadeLastGroup || null;
          try {
            if (!lastGroup && window.localStorage) {
              lastGroup = window.localStorage.getItem('shadeLastGroup');
            }
          } catch (e) {
            console.log(e);
          }
        }

        if (!lastGroup || lastGroup === 'All') {
          // Default state is already "All", nothing to do
          return;
        }

        const filtersContainer = this.querySelector('.filters');
        if (!filtersContainer) return;

        const filterEl = Array.from(filtersContainer.querySelectorAll('li'))
          .find(li => li.dataset.group === lastGroup);

        if (!filterEl) return;

        this.onFilterClick(filterEl);
      }

      onClick(e) {
        // Handle filter clicks
        const filter = e.target.closest('.filters li');
        if (filter && this.contains(filter)) {
          e.preventDefault();
          this.onFilterClick(filter);
          return;
        }

        // Handle shade clicks
        const li = e.target.closest('.available-shades__elements');
        if (!li || !this.contains(li)) return;
        const url = li.dataset.productUrl;
        if (!url) return;
        if (li.classList.contains('selected')) return;

        this.swapShade(url);
      }

      onFilterClick(filterEl) {
        const group = filterEl.dataset.group;

        window.__shadeLastGroup = group;
        try {
          window.localStorage.setItem('shadeLastGroup', group);
        } catch (e) {
          console.log(e);
        }

        const items = this.querySelectorAll('.available-shades__elements');

        // Show/hide shades based on data-group
        items.forEach((item) => {
          const itemGroup = item.dataset.group;
          const showAll = !group || group === 'All';
          const shouldShow = showAll || itemGroup === group;
          item.hidden = !shouldShow;
        });

        // Update selected class on filters
        const filtersContainer = filterEl.parentElement;
        if (!filtersContainer) return;

        filtersContainer.querySelectorAll('li').forEach((li) => {
          li.classList.add('button--secondary');
        });
        filterEl.classList.remove('button--secondary');

        const isExpanded = this.summaryButton.getAttribute('aria-expanded') === 'true';
        if (!isExpanded) {
          this.setSummaryExpanded(true);
          this.summaryTarget.classList.toggle('hidden');
        }

        // // Move clicked filter to second position (keep "All" as first)
        // const first = filtersContainer.firstElementChild;
        // if (first && filterEl !== first) {
        //   filtersContainer.insertBefore(filterEl, first.nextElementSibling);
        // }
      }

      async swapShade(productUrl) {
        this.startLoading();
        try { this.controller?.abort(); } catch (_) { }
        this.controller = new AbortController();

        const params = new URLSearchParams();
        params.set('sections', this.sections.join(','));
        const fetchUrl = `${productUrl}${productUrl.includes('?') ? '&' : '?'}${params.toString()}`;

        try {
          const res = await fetch(fetchUrl, { signal: this.controller.signal, credentials: 'same-origin' });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const payload = await res.json();

          this.sections.forEach((key) => {
            const target = document.querySelector(`[data-section="${key}"]`);
            const html = payload[key];
            if (!target || !html) return;

            const newRoot = new DOMParser().parseFromString(html, 'text/html');
            const newSection = newRoot.querySelector(`[data-section="${key}"]`) || newRoot.body;

            const patchedAny = PATCH_REGIONS
              .map((region) => this.patchRegion(target, newSection, region))
              .some(Boolean);
            const formPatched = this.patchProductForm(target, newSection);

            if (!patchedAny && !formPatched) {
              target.innerHTML = html;
            }

            const pageTitle = this.getPageTitleFromPayload(html) || document.title;
            this.updateHistory(productUrl, pageTitle);
          });
        } catch (err) {
          if (err.name !== 'AbortError') console.error('[ShadeSelector] swap error:', err);
        } finally {
          if (window.yotpoWidgetsContainer?.initWidgets) {
            window.yotpoWidgetsContainer.initWidgets();
          }
          if (window.Trustpilot?.loadFromElement) {
            document.querySelectorAll('.trustpilot-widget').forEach(el => window.Trustpilot.loadFromElement(el));
            // window.Trustpilot.loadFromElement(document.querySelector('.trustpilot-widget'));
          }
          this.stopLoading();

          document.dispatchEvent(new Event('shades:updated'));
        }
      }

      // Patches all elements matching region.selector inside oldRoot with the
      // matching elements from newRoot (paired by id where available, else by
      // position). Returns true if anything was patched.
      patchRegion(oldRoot, newRoot, region) {
        const oldEls = Array.from(oldRoot.querySelectorAll(region.selector));
        if (!oldEls.length) return false;
        const newEls = Array.from(newRoot.querySelectorAll(region.selector));
        if (!newEls.length) return false;

        let patched = false;
        oldEls.forEach((oldEl, index) => {
          const newEl = (oldEl.id && newEls.find((el) => el.id === oldEl.id)) || newEls[index];
          if (!newEl) return;

          if (region.mode === 'inner') {
            oldEl.innerHTML = newEl.innerHTML;
          } else {
            oldEl.outerHTML = newEl.outerHTML;
          }
          patched = true;
        });
        return patched;
      }

      // Updates the add-to-cart form's hidden fields and submit-button state in
      // place instead of replacing the <form>/<product-form> nodes, so apps that
      // bind listeners directly to those elements (e.g. an out-of-stock widget)
      // keep working. Mirrors global.js's updateVariantInput/updateVariantEventsInput,
      // which do the same thing for same-product variant changes.
      patchProductForm(oldRoot, newRoot) {
        const oldForm = oldRoot.querySelector('product-form form');
        const newForm = newRoot.querySelector('product-form form');
        if (!oldForm || !newForm) return false;

        newForm.querySelectorAll('input[type="hidden"]').forEach((newInput) => {
          if (!newInput.name) return;
          const oldInput = oldForm.querySelector(`input[type="hidden"][name="${newInput.name}"]`);
          if (!oldInput || oldInput.value === newInput.value) return;
          oldInput.value = newInput.value;
          oldInput.dispatchEvent(new Event('change', { bubbles: true }));
        });

        const oldSubmit = oldForm.querySelector('[type="submit"]');
        const newSubmit = newForm.querySelector('[type="submit"]');
        if (oldSubmit && newSubmit) {
          oldSubmit.disabled = newSubmit.disabled;
          const oldLabel = oldSubmit.querySelector('span');
          const newLabel = newSubmit.querySelector('span');
          if (oldLabel && newLabel) oldLabel.textContent = newLabel.textContent;
        }

        return true;
      }

      startLoading() {
        document.body.classList.add('loader-open');
        if (!document.querySelector('.fullscreen-loader')) {
          const loader = document.createElement('div');
          loader.className = 'fullscreen-loader';
          document.body.appendChild(loader);
        }
      }

      stopLoading() {
        document.body.classList.remove('loader-open');
        const loader = document.querySelector('.fullscreen-loader');
        if (loader) loader.remove();
      }

      getPageTitleFromPayload(payload) {
        if (!payload || typeof payload !== 'string') return null;
        const doc = new DOMParser().parseFromString(payload, 'text/html');
        const sectionElement = doc.querySelector('[data-page-title]');
        if (sectionElement?.dataset?.pageTitle) return sectionElement.dataset.pageTitle.trim();
        return null;
      }

      updateHistory(url, pageTitle) {
        history.pushState({ shadeSwap: true }, pageTitle, url);
        if (pageTitle) document.title = pageTitle;
      }

      initSummaryClick() {
        if (this.summaryButton && this.summaryTarget) {
          this.summaryButton.addEventListener('click', (e) => {
            e.preventDefault();
            const isExpanded = this.summaryButton.getAttribute('aria-expanded') === 'true';
            this.setSummaryExpanded(!isExpanded);
            this.summaryTarget.classList.toggle('hidden');
          });
        }
      }

      setSummaryExpanded(expanded) {
        if (!this.summaryButton) return;
        this.summaryButton.setAttribute('aria-expanded', String(expanded));
        if (this.summaryLabel) {
          const label = expanded
            ? this.summaryButton.dataset.labelExpanded
            : this.summaryButton.dataset.labelCollapsed;
          if (label) this.summaryLabel.textContent = label;
        }
      }
    }
  );
}


document.addEventListener('DOMContentLoaded', () => {
  if (window.__shadeTooltipInit) return;
  window.__shadeTooltipInit = true;

  const SELECTOR = '.available-shades__elements';
  const OFFSET_Y = 24;   // below cursor (centered horizontally)
  const LERP = 0.25; // easing

  function ensureTooltip() {
    const all = Array.from(document.querySelectorAll('#shade-tooltip'));
    let el = all[0];
    if (!el) {
      el = document.createElement('div');
      el.id = 'shade-tooltip';
      document.body.appendChild(el);
    } else if (el.parentNode !== document.body) {
      document.body.appendChild(el);
    }
    for (let i = 1; i < all.length; i++) all[i].remove();
    el.style.position = 'absolute';
    el.style.left = '0px';
    el.style.top = '0px';
    el.style.pointerEvents = 'none';
    el.style.zIndex = '2147483647';
    return el;
  }

  let tooltip = ensureTooltip();

  // LERP state
  let targetX = 0, targetY = 0;
  let curX = 0, curY = 0;
  let current = null;

  function clampBelowCentered(pageX, pageY) {
    const w = tooltip.offsetWidth || 0;
    const h = tooltip.offsetHeight || 0;
    const sx = window.scrollX || 0;
    const sy = window.scrollY || 0;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let x = pageX - (w / 2);
    let y = pageY + OFFSET_Y;

    if (x < sx) x = sx;
    if (x + w > sx + vw) x = sx + vw - w;
    if (y + h > sy + vh) y = pageY - h - OFFSET_Y; // flip above if needed
    if (y < sy) y = sy;

    return [x, y];
  }

  function setTarget(pageX, pageY) {
    const [x, y] = clampBelowCentered(pageX, pageY);
    targetX = x; targetY = y;
  }

  function tick() {
    curX += (targetX - curX) * LERP;
    curY += (targetY - curY) * LERP;
    // uses your CSS vars → transform: translate3d(var(--x), var(--y), 0)
    tooltip.style.setProperty('--x', curX + 'px');
    tooltip.style.setProperty('--y', curY + 'px');
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);

  // Delegated listeners
  document.addEventListener('mouseover', (e) => {
    const li = e.target.closest(SELECTOR);
    if (!li) return;
    current = li;

    tooltip = ensureTooltip();

    const shade = li.getAttribute('data-shade')
      || li.getAttribute('aria-label')
      || (li.textContent || '').trim();

    tooltip.textContent = shade || '';
    tooltip.classList.add('is-visible');

    setTarget(e.pageX, e.pageY);
    curX = targetX; curY = targetY; // snap first frame
    tooltip.style.setProperty('--x', curX + 'px');
    tooltip.style.setProperty('--y', curY + 'px');
  }, { passive: true });

  document.addEventListener('mousemove', (e) => {
    if (!current) return;
    setTarget(e.pageX, e.pageY);
  }, { passive: true });

  document.addEventListener('mouseout', (e) => {
    if (!current) return;
    const to = e.relatedTarget;
    if (to && (to === current || (to.closest && to.closest(SELECTOR) === current))) return;
    current = null;
    tooltip.classList.remove('is-visible');
  }, { passive: true });

  document.addEventListener('shades:updated', () => {
    tooltip = ensureTooltip();
    // reset any stale hover from removed nodes
    current = null;
    tooltip.classList.remove('is-visible');
  });

  const mo = new MutationObserver(() => {
    tooltip = ensureTooltip();
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
});